import React, { useState, useEffect } from "react";
import {
  ArrowRight,
  Star,
  Menu,
  X,
  ShieldCheck,
  Zap,
  Megaphone,
  ShoppingBag,
  Eye,
  MapPin,
  Sparkles,
  Trophy,
  BarChart3,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import logoMarca from "@/assets/logoMarca.png";
import { SystemMockup } from "@/ui/components/landing/SystemMockup";
import { Button } from "@/ui/components/ui/button";

const Index = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleStartLearning = () => navigate("/cadastro");
  const handleLogin = () => navigate("/login");

  return (
    <div className="font-sans antialiased bg-[#0a0a0a] text-white selection:bg-brand-accent selection:text-white overflow-x-hidden">
      {/* --- Noise Texture Overlay --- */}
      <div
        className="fixed inset-0 pointer-events-none z-[9999] opacity-[0.03] text-transparent"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='1'/%3E%3C/svg%3E")`,
        }}
      ></div>

      {/* --- Navbar --- */}
      <nav
        className={`fixed top-0 w-full z-50 transition-all duration-300 border-b border-white/[0.08] ${
          scrolled
            ? "bg-[#0a0a0a]/90 backdrop-blur-xl py-3"
            : "bg-transparent py-5"
        }`}
      >
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div
              className="flex items-center gap-3 cursor-pointer group"
              onClick={() => navigate("/")}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-brand-accent/20 blur-lg rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                <img
                  src={logoMarca}
                  alt="Engenharia do SIM"
                  className="w-10 h-10 rounded-lg relative z-10"
                />
              </div>
              <span className="text-xl font-bold tracking-tight text-white group-hover:text-brand-accent-light transition-colors">
                Engenharia do SIM
              </span>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {["Metodologia", "Plataforma"].map((item) => (
                <a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  className="text-sm font-medium text-white/70 hover:text-white transition-colors"
                >
                  {item}
                </a>
              ))}
            </div>

            {/* Actions */}
            <div className="hidden md:flex items-center gap-4">
              <button
                onClick={handleLogin}
                className="text-sm font-medium text-white/90 hover:text-brand-accent transition-colors"
              >
                Entrar
              </button>
              <Button
                onClick={handleStartLearning}
                className="bg-brand-accent hover:bg-brand-accent-light text-black font-semibold rounded-full px-6 transition-all duration-300 transform hover:scale-105"
              >
                Começar agora
              </Button>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </nav>

      {/* --- Mobile Menu --- */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 bg-[#0a0a0a] pt-24 px-6 md:hidden">
          <div className="flex flex-col gap-6 text-xl">
            {["Metodologia", "Plataforma"].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                onClick={() => setMobileMenuOpen(false)}
                className="text-white/80 hover:text-brand-accent border-b border-white/10 pb-4"
              >
                {item}
              </a>
            ))}
            <Button
              onClick={handleStartLearning}
              className="w-full bg-brand-accent text-black mt-4"
            >
              Começar agora
            </Button>
          </div>
        </div>
      )}

      {/* --- Hero Section --- */}
      <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden">
        {/* Ambient Background Glows */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-brand-primary-dark opacity-40 blur-[120px] rounded-full pointer-events-none -z-10" />
        <div className="absolute top-[20%] right-[-10%] w-[600px] h-[600px] bg-brand-accent opacity-5 blur-[100px] rounded-full pointer-events-none -z-10" />

        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex flex-col items-center text-center max-w-4xl mx-auto mb-16 md:mb-24">
            {/* Headline */}
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white tracking-tight leading-[1.05] mb-8 animate-fadeInUp animation-delay-100 text-balance">
              Engenharia <br className="hidden md:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-white to-white/50">
                do SIM
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-white/60 max-w-2xl leading-relaxed mb-10 animate-fadeInUp animation-delay-200 text-balance">
              O sistema que transforma sua expertise em um negócio digital
              previsível — com{" "}
              <span className="text-white font-medium">
                metodologia validada
              </span>
              ,{" "}
              <span className="text-white font-medium">
                inteligência artificial
              </span>{" "}
              e uma{" "}
              <span className="text-white font-medium">
                jornada gamificada passo a passo
              </span>
              .
            </p>

            {/* CTAs */}
            <div className="animate-fadeInUp animation-delay-300 w-full sm:w-auto">
              <Button
                onClick={handleStartLearning}
                className="h-14 px-8 text-base bg-white text-black hover:bg-gray-100 font-semibold rounded-full w-full sm:w-auto shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all hover:shadow-[0_0_30px_rgba(255,255,255,0.4)]"
              >
                Começar agora
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Social Proof */}
          <div className="mt-20 pt-10 border-t border-white/5 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { label: "Alunos Ativos", value: "30,000+" },
              { label: "Faturamento Gerado", value: "R$ 200mi+" },
              { label: "Satisfação", value: "4.9/5.0" },
              { label: "Nichos Validados", value: "50+" },
            ].map((stat, i) => (
              <div key={i} className="text-center md:text-left">
                <h3 className="text-3xl font-bold text-white mb-1">
                  {stat.value}
                </h3>
                <p className="text-sm text-white/50 uppercase tracking-wider font-medium">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- O Problema --- */}
      <section className="py-24 md:py-32 bg-[#050505] relative">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              Você já tem o conhecimento.
              <br />
              <span className="text-brand-accent">
                O que falta é o sistema.
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              {
                icon: Megaphone,
                title: "Conteúdo sem estratégia",
                desc: "Posta todo dia, mas não sabe se está atraindo o público certo.",
              },
              {
                icon: ShoppingBag,
                title: "Oferta genérica",
                desc: "Seu produto é bom, mas o cliente não entende por que deveria escolher você.",
              },
              {
                icon: Eye,
                title: "Crescimento no escuro",
                desc: "Não sabe o que está funcionando, o que mudar, nem qual o próximo passo.",
              },
            ].map((card, i) => (
              <div
                key={i}
                className="p-6 rounded-2xl bg-white/5 border border-white/5 backdrop-blur-sm"
              >
                <div className="w-12 h-12 rounded-xl bg-brand-accent/10 flex items-center justify-center mb-4">
                  <card.icon className="w-6 h-6 text-brand-accent" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">
                  {card.title}
                </h3>
                <p className="text-sm text-white/50 leading-relaxed">
                  {card.desc}
                </p>
              </div>
            ))}
          </div>

          <p className="text-center text-lg text-white/60">
            A Engenharia do SIM existe para resolver exatamente isso.
          </p>
        </div>
      </section>

      {/* --- Methodology Section --- */}
      <section
        id="metodologia"
        className="py-24 md:py-32 bg-[#0a0a0a] relative"
      >
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Não é mágica.
              <br />É <span className="text-brand-accent">Engenharia</span>.
            </h2>
            <p className="text-lg text-white/60 leading-relaxed">
              Um método em 3 pilares para transformar sua expertise em um
              negócio que atrai, convence e vende — de forma previsível.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              {
                letter: "S",
                title: "Sede de Mudança",
                desc: "Identifique a dor latente do seu mercado e canalize a demanda que já existe para a sua solução.",
                icon: Zap,
              },
              {
                letter: "I",
                title: "Identidade Única",
                desc: "Construa um posicionamento magnético que torna a concorrência irrelevante.",
                icon: ShieldCheck,
              },
              {
                letter: "M",
                title: "Mensagem Memorável",
                desc: "Transforme complexidade em narrativas simples que grudam na mente do seu cliente.",
                icon: Star,
              },
            ].map((card, i) => (
              <div
                key={i}
                className="group p-6 rounded-2xl bg-white/5 border border-white/5 hover:border-brand-accent/30 hover:bg-white/10 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-brand-surface-dark border border-white/10 flex items-center justify-center text-2xl font-bold text-brand-accent shadow-lg group-hover:scale-110 transition-transform">
                    {card.letter}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      {card.title}
                    </h3>
                    <p className="text-sm text-white/50 leading-relaxed group-hover:text-white/70 transition-colors">
                      {card.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-lg text-white/60">
            Esse é o framework. A plataforma é onde você coloca ele em prática.
          </p>
        </div>
      </section>

      {/* --- Como Funciona --- */}
      <section className="py-24 md:py-32 bg-[#050505] relative">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Sua jornada em{" "}
              <span className="text-brand-accent">3 movimentos</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Steps */}
            <div className="space-y-8">
              {[
                {
                  number: "01",
                  title: "Posicione-se",
                  desc: "Descubra seu posicionamento único no mercado com a ajuda da nossa IA. Em poucos minutos, você terá clareza sobre o que te diferencia.",
                },
                {
                  number: "02",
                  title: "Construa seus ativos",
                  desc: "Siga a jornada dos 7 Templos — cada etapa gera um ativo real do seu negócio: da sua mensagem ao seu funil de vendas.",
                },
                {
                  number: "03",
                  title: "Escale com método",
                  desc: "Com gamificação, streaks e IA que te guia, você mantém consistência e evolui sem depender de inspiração.",
                },
              ].map((step, i) => (
                <div key={i} className="flex gap-5">
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-brand-accent/10 border border-brand-accent/20 flex items-center justify-center">
                    <span className="text-xl font-bold text-brand-accent">
                      {step.number}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-white/50 leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* SystemMockup */}
            <div className="relative">
              <div className="relative z-10 rounded-xl overflow-hidden shadow-2xl border border-white/10 bg-[#0f0f0f]">
                <div className="absolute inset-0 bg-gradient-to-tr from-brand-accent/5 via-transparent to-brand-primary/20 pointer-events-none z-20 mix-blend-overlay" />
                <SystemMockup />
              </div>
              <div className="absolute -inset-4 bg-brand-accent/20 blur-[100px] -z-10 opacity-40 rounded-full top-1/2" />
            </div>
          </div>
        </div>
      </section>

      {/* --- Ecossistema --- */}
      <section id="plataforma" className="py-24 bg-[#0a0a0a]">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Tudo o que você precisa.{" "}
              <span className="text-brand-accent">Em um só lugar.</span>
            </h2>
            <p className="text-lg text-white/60">
              Cada peça foi desenhada para funcionar junto — método, tecnologia
              e gamificação integrados.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                icon: MapPin,
                title: "7 Templos",
                desc: "Jornada estruturada do posicionamento às ofertas. Cada templo gera um ativo real do seu negócio.",
              },
              {
                icon: Sparkles,
                title: "IA que constrói com você",
                desc: "Wizards inteligentes que te guiam na criação de posicionamento, mapeamento de cliente e mais — sem precisar começar do zero.",
              },
              {
                icon: Trophy,
                title: "Gamificação real",
                desc: "XP, níveis, streaks e conquistas que transformam aprendizado em hábito. Você evolui sem depender de motivação.",
              },
              {
                icon: BarChart3,
                title: "Dashboard estratégico",
                desc: "Acompanhe seu progresso, veja métricas do seu negócio e saiba exatamente onde está e o que fazer em seguida.",
              },
            ].map((card, i) => (
              <div
                key={i}
                className="p-8 rounded-3xl bg-[#121212] border border-white/10 group hover:border-brand-accent/30 transition-colors"
              >
                <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-brand-accent/20 group-hover:text-brand-accent transition-colors">
                  <card.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  {card.title}
                </h3>
                <p className="text-sm text-white/50 leading-relaxed">
                  {card.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- CTA Section --- */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-brand-primary-dark">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-brand-accent/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-brand-primary-light/20 rounded-full blur-[120px]" />
        </div>

        <div className="container mx-auto px-6 relative z-10 text-center">
          <h2 className="text-5xl md:text-7xl font-bold text-white tracking-tighter mb-8">
            Comece sua <br />
            jornada agora.
          </h2>
          <p className="text-xl text-white/60 mb-12 max-w-2xl mx-auto">
            Acesso imediato à plataforma completa — metodologia, IA e
            gamificação trabalhando juntos por você.
          </p>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
            <Button
              onClick={handleStartLearning}
              className="h-16 px-10 text-lg bg-brand-accent hover:bg-brand-accent-light text-black font-bold rounded-full shadow-[0_0_40px_rgba(201,162,39,0.3)] hover:shadow-[0_0_60px_rgba(201,162,39,0.5)] transition-all transform hover:-translate-y-1"
            >
              Criar minha conta
            </Button>
            <p className="text-sm text-white/40 mt-4 sm:mt-0">
              Acesso imediato • 7 dias de garantia
            </p>
          </div>
        </div>
      </section>

      {/* --- Footer --- */}
      <footer className="bg-black py-12 border-t border-white/10">
        <div className="container mx-auto px-6 max-w-7xl flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-2">
            <img src={logoMarca} alt="Logo" className="w-8 h-8 opacity-80" />
            <span className="text-white/80 font-bold">Engenharia do SIM</span>
          </div>

          <div className="flex gap-8 text-sm text-white/50">
            <a href="#" className="hover:text-white transition-colors">
              Termos de Uso
            </a>
            <a href="#" className="hover:text-white transition-colors">
              Privacidade
            </a>
            <a href="#" className="hover:text-white transition-colors">
              Suporte
            </a>
          </div>

          <div className="text-sm text-white/30">
            © {new Date().getFullYear()} Engenharia do SIM. Todos os direitos
            reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
